import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface Task {
  id: string;
  task_type: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled' | 'retrying';
  priority: number;
  progress: {
    current: number;
    total: number;
    message?: string;
    percentage: number;
  };
  created_at: string;
  started_at?: string;
  completed_at?: string;
  error_message?: string;
  retry_count: number;
  result?: string;
}

interface TaskStore {
  tasks: Map<string, Task>;
  activeTasks: string[];
  webSockets: Map<string, WebSocket>;
  
  // Actions
  addTask: (task: Task) => void;
  updateTask: (taskId: string, updates: Partial<Task>) => void;
  removeTask: (taskId: string) => void;
  cancelTask: (taskId: string) => Promise<void>;
  connectToTask: (taskId: string) => void;
  disconnectFromTask: (taskId: string) => void;
  loadTasks: () => Promise<void>;
  cleanupStaleActiveTasks: () => void;
  
  // Getters
  getActiveTasksCount: () => number;
  getTasksByStatus: (status: Task['status']) => Task[];
  getDebugInfo: () => any;
}

export const useTaskStore = create<TaskStore>()(
  persist(
    (set, get) => ({
      tasks: new Map(),
      activeTasks: [],
      webSockets: new Map(),
      
      addTask: (task) => {
        set((state) => {
          const tasks = new Map(state.tasks);
          tasks.set(task.id, task);
          
          const activeTasks = [...state.activeTasks];
          if (!activeTasks.includes(task.id) && 
              ['pending', 'processing', 'retrying'].includes(task.status)) {
            activeTasks.push(task.id);
          }
          
          return { tasks, activeTasks };
        });
        
        // Auto-connect to WebSocket for active tasks
        if (['pending', 'processing', 'retrying'].includes(task.status)) {
          get().connectToTask(task.id);
        }
      },
      
      updateTask: (taskId, updates) => {
        set((state) => {
          const tasks = new Map(state.tasks);
          const existingTask = tasks.get(taskId);
          
          if (existingTask) {
            const updatedTask = { ...existingTask, ...updates };
            tasks.set(taskId, updatedTask);
            
            // Handle status changes
            if (existingTask.status !== updatedTask.status) {
              // Trigger SOV files refresh when document parsing completes
              if (updatedTask.task_type === 'document_parse' && updatedTask.status === 'completed') {
                window.dispatchEvent(new CustomEvent('document-parse-completed', {
                  detail: { taskId, task: updatedTask }
                }));
                
                // Show success toast notification
                try {
                  const result = updatedTask.result ? JSON.parse(updatedTask.result) : null;
                  const propertiesCount = result?.properties_count || 0;
                  
                  window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: {
                      id: `task-completed-${taskId}`,
                      type: 'success',
                      title: 'Document parsing completed!',
                      message: `Successfully parsed ${propertiesCount} properties`,
                      duration: 6000
                    }
                  }));
                } catch (error) {
                  console.warn('Failed to parse task result for toast notification:', error);
                  window.dispatchEvent(new CustomEvent('show-toast', {
                    detail: {
                      id: `task-completed-${taskId}`,
                      type: 'success',
                      title: 'Document parsing completed!',
                      duration: 5000
                    }
                  }));
                }
              }
              
              // Show failure toast notification for document parsing
              if (updatedTask.task_type === 'document_parse' && updatedTask.status === 'failed') {
                window.dispatchEvent(new CustomEvent('show-toast', {
                  detail: {
                    id: `task-failed-${taskId}`,
                    type: 'error',
                    title: 'Document parsing failed',
                    message: updatedTask.error_message || 'An error occurred during processing',
                    duration: 8000
                  }
                }));
              }
            }
            
            // Log status changes for debugging (no sound notifications)
            // Note: Sound notifications removed per user request
            
            // Update active tasks list
            let activeTasks = [...state.activeTasks];
            const isActive = ['pending', 'processing', 'retrying'].includes(updatedTask.status);
            const wasActive = activeTasks.includes(taskId);
            
            if (isActive && !wasActive) {
              activeTasks.push(taskId);
            } else if (!isActive && wasActive) {
              activeTasks = activeTasks.filter(id => id !== taskId);
              
              // Disconnect WebSocket for completed tasks
              get().disconnectFromTask(taskId);
              
              // Auto-remove completed tasks after 30 seconds
              if (updatedTask.status === 'completed') {
                setTimeout(() => {
                  get().removeTask(taskId);
                }, 30 * 1000);
              }
            }

            return { tasks, activeTasks };
          }
          
          return state;
        });
      },
      
      removeTask: (taskId) => {
        get().disconnectFromTask(taskId);
        
        set((state) => {
          const tasks = new Map(state.tasks);
          tasks.delete(taskId);
          
          const activeTasks = state.activeTasks.filter(id => id !== taskId);
          
          return { tasks, activeTasks };
        });
      },
      
      connectToTask: (taskId) => {
        const { webSockets } = get();
        
        // Don't reconnect if already connected
        if (webSockets.has(taskId)) {
          return;
        }
        
        // Determine WebSocket URL based on current location
        const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        const host = window.location.hostname;
        // Only add port for localhost, not for production domains
        const port = (host === 'localhost' || host === '127.0.0.1') ? ':8001' : '';
        const wsUrl = `${protocol}//${host}${port}/api/tasks/ws/${taskId}`;
        const ws = new WebSocket(wsUrl);
        
        ws.onopen = () => {
          console.log(`Connected to task ${taskId} WebSocket`);
        };
        
        ws.onmessage = (event) => {
          try {
            const taskData = JSON.parse(event.data);
            get().updateTask(taskId, taskData);
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error);
          }
        };
        
        ws.onerror = (error) => {
          console.error(`WebSocket error for task ${taskId}:`, error);
        };
        
        ws.onclose = () => {
          console.log(`Disconnected from task ${taskId} WebSocket`);
          set((state) => {
            const webSockets = new Map(state.webSockets);
            webSockets.delete(taskId);
            return { webSockets };
          });
        };
        
        set((state) => {
          const webSockets = new Map(state.webSockets);
          webSockets.set(taskId, ws);
          return { webSockets };
        });
      },
      
      disconnectFromTask: (taskId) => {
        const { webSockets } = get();
        const ws = webSockets.get(taskId);
        
        if (ws) {
          ws.close();
          set((state) => {
            const webSockets = new Map(state.webSockets);
            webSockets.delete(taskId);
            return { webSockets };
          });
        }
      },
      
      loadTasks: async () => {
        try {
          // Import apiService dynamically to avoid circular dependencies
          const { apiService } = await import('../services/api');
          const data = await apiService.listTasks();
          
          const tasks = new Map<string, Task>();
          const activeTasks: string[] = [];
          
          data.tasks.forEach((task: Task) => {
            tasks.set(task.id, task);
            if (['pending', 'processing', 'retrying'].includes(task.status)) {
              activeTasks.push(task.id);
            }
          });

          set({ tasks, activeTasks });
          
          // Clean up any stale active tasks after loading
          get().cleanupStaleActiveTasks();
          
          // Connect to active tasks
          activeTasks.forEach(taskId => {
            get().connectToTask(taskId);
          });
          
          // Set up periodic cleanup every 30 seconds
          const cleanupInterval = setInterval(() => {
            get().cleanupStaleActiveTasks();
          }, 30000);
          
          // Store interval for cleanup later (if needed)
          (window as any).__taskCleanupInterval = cleanupInterval;
          
        } catch (error) {
          console.error('Failed to load tasks:', error);
        }
      },
      
      getActiveTasksCount: () => {
        return get().activeTasks.length;
      },
      
      getTasksByStatus: (status) => {
        const tasks = Array.from(get().tasks.values());
        return tasks.filter(task => task.status === status);
      },
      
      cancelTask: async (taskId: string) => {
        try {
          // Import apiService dynamically to avoid circular dependencies
          const { apiService } = await import('../services/api');
          await apiService.cancelTask(taskId);
          
          // Update task status to cancelled
          get().updateTask(taskId, { status: 'cancelled' });
        } catch (error) {
          console.error('Failed to cancel task:', taskId, error);
          throw error;
        }
      },
      
      cleanupStaleActiveTasks: () => {
        set((state) => {
          const { tasks, activeTasks } = state;
          
          // Remove active task IDs that don't exist in tasks or are no longer active
          const validActiveTasks = activeTasks.filter(taskId => {
            const task = tasks.get(taskId);
            return task && ['pending', 'processing', 'retrying'].includes(task.status);
          });
          
          return { ...state, activeTasks: validActiveTasks };
        });
      },
      
      getDebugInfo: () => {
        const { tasks, activeTasks, webSockets } = get();
        const tasksByStatus = {
          pending: 0,
          processing: 0,
          retrying: 0,
          completed: 0,
          failed: 0,
          cancelled: 0
        };
        
        const allTasks = Array.from(tasks.values());
        allTasks.forEach(task => {
          if (tasksByStatus.hasOwnProperty(task.status)) {
            tasksByStatus[task.status]++;
          }
        });
        
        return {
          totalTasks: allTasks.length,
          activeTaskIds: activeTasks,
          activeTaskCount: activeTasks.length,
          webSocketCount: webSockets.size,
          tasksByStatus,
          activeTasksDetails: activeTasks.map(id => {
            const task = tasks.get(id);
            return task ? { id, status: task.status, type: task.task_type } : { id, missing: true };
          }),
          orphanedActiveTasks: activeTasks.filter(id => {
            const task = tasks.get(id);
            return !task || !['pending', 'processing', 'retrying'].includes(task.status);
          })
        };
      }
    }),
    {
      name: 'task-storage',
      // Don't persist anything to avoid stale data issues
      partialize: (state) => ({}),
      // Clear storage on hydration to start fresh
      onRehydrateStorage: () => (state, error) => {
        // Always start with empty state to avoid stale data
        return {
          tasks: new Map(),
          activeTasks: [],
          webSockets: new Map()
        };
      }
    }
  )
);